import * as Sentry from "@sentry/nextjs";
import { makeAutoObservable, runInAction } from "mobx";

import { followTeam, getApiErrorMessage, getTeam, unfollowTeam } from "@/api";
import { TeamModel } from "@/models/TeamModel";

import type { Folder, DocumentShort } from "@/api";
import type { RootStore } from "@/store/RootStore";

type TeamStatus = "idle" | "loading" | "loaded" | "error";
type FollowingStatus = "idle" | "loading" | "loaded" | "error";

export class PublicProfileStore {
  rootStore: RootStore;

  isOpen = false;

  team?: TeamModel = undefined;

  published?: Folder = undefined;

  shared: DocumentShort[] = [];

  teamStatus: TeamStatus = "idle";

  followingStatus: FollowingStatus = "idle";

  error?: string = undefined;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  get isOurProfile(): boolean | undefined {
    if (this.rootStore.teams.fetchingProcess || !this.team) {
      return undefined;
    }
    return !!this.rootStore.teams.userTeams.find((team) => team.uid === this.team?.uid);
  }

  setTeamStatus(status: TeamStatus) {
    this.teamStatus = status;
  }

  setFollowingStatus(status: FollowingStatus) {
    this.followingStatus = status;
  }

  setError(error: string) {
    this.error = error;
  }

  async toggleFollow() {
    if (!this.team) {
      return;
    }
    this.setFollowingStatus("loading");
    try {
      if (this.team.following) {
        await unfollowTeam(this.team.permalink);
      } else {
        await followTeam(this.team.permalink);
      }
      const { team } = await getTeam(this.team.permalink);
      if (team) {
        runInAction(() => {
          this.team = new TeamModel(this.rootStore, team);
        });
      }
      this.setFollowingStatus("loaded");
    } catch (err) {
      this.setFollowingStatus("error");
      this.setError(getApiErrorMessage(err));
      Sentry.captureException(err);
    }
  }

  async fetchTeam(teamPermalink: string, signal?: AbortSignal) {
    if (teamPermalink === this.team?.permalink) {
      return;
    }
    try {
      this.setTeamStatus("loading");
      const { team } = await getTeam(teamPermalink, { signal });
      if (team) {
        runInAction(() => {
          this.team = new TeamModel(this.rootStore, team);
          this.published = team.published;
          this.shared = team.shared || [];
          this.setTeamStatus("loaded");
        });
      }
    } catch (err) {
      this.setTeamStatus("error");
      this.setError(getApiErrorMessage(err));
      Sentry.captureException(err);
    }
  }

  dispose() {
    this.team = undefined;
    this.published = undefined;
    this.teamStatus = "idle";
    this.followingStatus = "idle";
    this.error = undefined;
  }
}
